/**
 * Schema Converter - Convert between FormSchemaDefinition and internal field format
 * Updated for API v3.0 with typesafe FormSchemaDefinition structure
 */

import { FieldType } from '../types/enums';
import { LUCIDE_ICON_MAP } from './iconUtils';
import { resolveType } from '../registry/resolvers';
import { toPortalInternal, fromPortalInternal } from '../registry/portalAdapter';

/**
 * Map a Portal internal field type ("input", "image_camera", etc.) to the
 * canonical API FieldType enum string. Resolution goes through the unified
 * field type registry — see `src/registry/portalAdapter.js`.
 */
const mapInternalTypeToApiType = (internalType) => {
  return fromPortalInternal(internalType) || FieldType.TEXT;
};

/**
 * Map a canonical API FieldType enum string ("Image", "RepeatedForm") to the
 * Portal internal form-builder type string ("image_camera", "repeated_form").
 */
const mapApiTypeToInternalType = (apiType) => {
  return toPortalInternal(apiType) || 'input';
};

/**
 * Convert FormSchemaDefinition (from API) to internal field array format
 * @param {import('../types/formSchema').FormSchemaDefinition} schemaDefinition - API schema definition
 * @returns {Array} Array of internal field objects
 */
export const convertFormSchemaDefinitionToFields = (schemaDefinition) => {
  if (!schemaDefinition || !schemaDefinition.fields) {
    return [];
  }

  // Handle different serialization formats
  let fieldsArray = schemaDefinition.fields;

  // Check if fields is an object with $values (common in .NET serialization)
  if (fieldsArray && typeof fieldsArray === 'object' && !Array.isArray(fieldsArray)) {
    if (fieldsArray.$values) {
      fieldsArray = fieldsArray.$values;
    } else {
      // Convert object with numeric keys to array
      const keys = Object.keys(fieldsArray).filter(key => !isNaN(key)).sort((a, b) => Number(a) - Number(b));
      if (keys.length > 0) {
        fieldsArray = keys.map(key => fieldsArray[key]);
      } else {
        console.error('Invalid fields format - not an array and no numeric keys:', fieldsArray);
        return [];
      }
    }
  }

  if (!Array.isArray(fieldsArray)) {
    console.error('Fields is not an array after conversion:', fieldsArray);
    return [];
  }

  const seenKeys = new Set();
  return fieldsArray.map(apiField => {
    const internalType = mapApiTypeToInternalType(apiField.type);

    // Deduplicate keys
    let key = apiField.key;
    const baseKey = key;
    let counter = 1;
    while (seenKeys.has(key)) {
      key = `${baseKey}_${counter}`;
      counter++;
    }
    seenKeys.add(key);

    // If a Text field has isSampleId, display as sample_id component in builder
    const effectiveType = (apiField.isSampleId && internalType === 'input') ? 'input' : internalType;
    const effectiveComponentId = (apiField.isSampleId && internalType === 'input') ? 'sample_id' : internalType;

    // Base field structure
    const field = {
      id: key,
      type: effectiveType,
      componentId: effectiveComponentId,
      props: {
        name: key,
        label: apiField.title,
        placeholder: apiField.placeholder || '',
        required: apiField.required || false,
        disabled: apiField.disabled || false,
        nameManuallySet: true,
        isSampleId: apiField.isSampleId || false,
        isHidden: apiField.isHidden || false,
        labResultSource: apiField.labResultSource || null,
      }
    };

    // Add configuration-specific props
    const config = apiField.configuration || {};

    // Handle options for select/radio/checkbox/tags
    if (config.options) {
      let optionsArray = config.options;

      // Handle .NET serialization format for options array
      if (!Array.isArray(optionsArray) && typeof optionsArray === 'object') {
        if (optionsArray.$values) {
          optionsArray = optionsArray.$values;
        } else {
          // Convert object with numeric keys to array
          const keys = Object.keys(optionsArray).filter(key => !isNaN(key)).sort((a, b) => Number(a) - Number(b));
          if (keys.length > 0) {
            optionsArray = keys.map(key => optionsArray[key]);
          }
        }
      }

      if (Array.isArray(optionsArray)) {
        field.props.options = optionsArray.map(opt => ({
          value: opt.value,
          label: opt.label,
          color: opt.color,
          description: opt.description,
          icon: opt.icon,
          disabled: opt.disabled || false,
          // Carry the cascade group tag through into the internal option shape.
          ...(opt.group !== undefined && opt.group !== null && { group: opt.group }),
        }));
      }
    }

    // Advanced Field Rules — copy the FormField-level typed `rules` array through
    // into the internal field props so the builder/renderer can act on it.
    if (Array.isArray(apiField.rules)) {
      field.props.rules = apiField.rules;
    }

    // Add numeric constraints
    if (config.min !== undefined) field.props.min = config.min;
    if (config.max !== undefined) field.props.max = config.max;
    if (config.step !== undefined) field.props.step = config.step;

    // Add text constraints
    if (config.minLength !== undefined) field.props.minLength = config.minLength;
    if (config.maxLength !== undefined) field.props.maxLength = config.maxLength;
    if (config.pattern !== undefined) field.props.pattern = config.pattern;

    // Handle field-specific properties
    switch (internalType) {
      case 'tags':
        if (config.allowCustomOptions !== undefined) field.props.allowCustom = config.allowCustomOptions;
        if (config.allowCustom !== undefined) field.props.allowCustom = config.allowCustom;
        if (config.maxTags !== undefined) field.props.maxTags = config.maxTags;
        break;

      case 'geolocation':
      case 'location':
        if (config.accuracyLevel) field.props.accuracy = config.accuracyLevel;
        break;

      case 'header':
        if (config.text) field.props.text = config.text;
        if (config.level) field.props.level = config.level;
        if (config.align) field.props.align = config.align;
        break;

      case 'textarea':
        if (config.minRows) field.props.rows = config.minRows;
        break;

      case 'rating':
        if (config.maxRating) field.props.maxRating = config.maxRating;
        if (config.ratingIcon) field.props.ratingIcon = config.ratingIcon;
        break;

      case 'slider':
        if (config.sliderLabels) field.props.sliderLabels = config.sliderLabels;
        break;

      case 'file':
      case 'image_camera':
        if (config.acceptedFileTypes) field.props.acceptedFileTypes = config.acceptedFileTypes;
        if (config.maxFileSizeMB) field.props.maxFileSizeMB = config.maxFileSizeMB;
        if (config.maxFiles) field.props.maxFiles = config.maxFiles;
        break;

      case 'qr_scanner':
      case 'barcode_scanner':
        // Parse validation rules from JSON string
        if (config.validationRules) {
          try {
            field.props.validationRules = typeof config.validationRules === 'string'
              ? JSON.parse(config.validationRules)
              : config.validationRules;
          } catch (e) {
            console.error('Failed to parse validation rules:', e);
            field.props.validationRules = { rules: [], allowEmpty: true };
          }
        } else {
          field.props.validationRules = { rules: [], allowEmpty: true };
        }
        // Barcode format
        if (internalType === 'barcode_scanner' && config.barcodeFormat) {
          field.props.barcodeFormat = config.barcodeFormat;
        }
        break;

      case 'date':
        field.type = 'datetime';
        field.componentId = 'datetime';
        field.props.precision = 'date';
        if (config.autoCapture !== undefined) field.props.autoCapture = config.autoCapture;
        break;
      case 'time':
        field.type = 'datetime';
        field.componentId = 'datetime';
        field.props.precision = 'time';
        if (config.autoCapture !== undefined) field.props.autoCapture = config.autoCapture;
        break;
      case 'datetime':
        field.props.precision = 'datetime';
        if (config.autoCapture !== undefined) field.props.autoCapture = config.autoCapture;
        break;

      case 'repeated_form':
        if (config.repeatedFormSchemaId) {
          field.props.configuration = {
            repeatedFormSchemaId: config.repeatedFormSchemaId,
            minInstances: config.minInstances || 0,
            maxInstances: config.maxInstances || 10,
            repeatedFormDisplayName: config.repeatedFormDisplayName || ''
          };
        }
        break;

      case 'voice':
        if (config.enableTranscription !== undefined) field.props.enableTranscription = config.enableTranscription;
        if (config.transcriptionLanguage) field.props.transcriptionLanguage = config.transcriptionLanguage;
        if (config.maxDurationSeconds !== undefined) field.props.maxDurationSeconds = config.maxDurationSeconds;
        if (config.quality) field.props.quality = config.quality;
        break;
    }

    // Sample id lab-integration affordance: read expectedLabResults off the sample id field.
    if (apiField.isSampleId && Array.isArray(config.expectedLabResults)) {
      field.props.expectedLabResults = config.expectedLabResults
        .filter(r => r && r.name)
        .map(r => ({ name: r.name, ...(r.unit && { unit: r.unit }) }));
    } else if (apiField.isSampleId) {
      field.props.expectedLabResults = [];
    }

    return field;
  });
};

/**
 * Derive the stable API field key from an internal builder field. The key is
 * normalized from the field name (falling back to the builder id) — Advanced
 * Field Rules reference sibling fields by this key.
 */
const deriveFieldKey = (field) => {
  const raw = String(field?.props?.name || field?.id || '');
  const validKey = raw.toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')   // Replace invalid chars with underscore
    .replace(/^[^a-z]/, 'field_')  // Ensure it starts with a lowercase letter
    .replace(/_+/g, '_')           // Collapse multiple underscores
    .replace(/^_+|_+$/g, '');      // Remove leading/trailing underscores
  return validKey || 'unnamed_field';
};

/**
 * Rewrite a rule array's sibling-field references from the builder's internal
 * field id to the stable API key. A reference that is already a key (a field
 * loaded from the API, where id === key) maps to itself; an unresolved
 * reference is left as-is so the API's schema validator can flag it.
 */
const remapRuleFieldRefs = (rules, keyForFieldId) => {
  const toKey = (ref) => (ref != null && keyForFieldId[ref]) || ref;
  return rules.map(rule => {
    if (!rule || typeof rule !== 'object') return rule;
    const next = { ...rule };
    if (next.controlledBy) {
      next.controlledBy = toKey(next.controlledBy);
    }
    if (next.when && Array.isArray(next.when.conditions)) {
      next.when = {
        ...next.when,
        conditions: next.when.conditions.map(cond =>
          (cond && cond.field ? { ...cond, field: toKey(cond.field) } : cond)
        ),
      };
    }
    return next;
  });
};

/**
 * Convert internal field array format to FormSchemaDefinition (for API)
 * @param {Array} fields - Array of internal field objects
 * @param {string} title - Form title
 * @param {string} [description] - Form description
 * @param {string} [icon] - Form icon
 * @returns {import('../types/formSchema').FormSchemaDefinition} API schema definition
 */
export const convertFieldsToFormSchemaDefinition = (fields, title, description = '', icon = 'clipboard-list') => {
  // Pre-compute every field's final, deduplicated API key. Advanced Field Rules
  // reference sibling fields by the builder's internal id; rules serialize using
  // the stable API key, so the id→key map below lets us rewrite those references.
  const keyForFieldId = {};
  const usedKeys = new Set();
  fields.forEach(field => {
    let finalKey = deriveFieldKey(field);
    const baseKey = finalKey;
    let counter = 1;
    while (usedKeys.has(finalKey)) {
      finalKey = `${baseKey}_${counter}`;
      counter++;
    }
    usedKeys.add(finalKey);
    keyForFieldId[field.id] = finalKey;
  });

  const apiFields = fields.map(field => {
    const apiType = mapInternalTypeToApiType(field.type);
    const finalKey = keyForFieldId[field.id];

    // Base API field structure
    const apiField = {
      key: finalKey,
      title: field.props.label || 'Untitled Field',
      type: apiType,
      required: field.props.required || false,
      disabled: field.props.disabled || false,
    };

    // Add optional fields
    if (field.props.placeholder) {
      apiField.placeholder = field.props.placeholder;
    }
    if (field.props.description) {
      apiField.description = field.props.description;
    }
    if (field.props.isSampleId) {
      apiField.isSampleId = true;
    }
    if (field.props.isHidden) {
      apiField.isHidden = true;
    }
    if (field.props.labResultSource &&
        field.props.labResultSource.sampleIdFieldKey &&
        field.props.labResultSource.analyteName) {
      apiField.labResultSource = {
        sampleIdFieldKey: field.props.labResultSource.sampleIdFieldKey,
        analyteName: field.props.labResultSource.analyteName,
        ...(field.props.labResultSource.unitHint && { unitHint: field.props.labResultSource.unitHint }),
      };
    }

    // Advanced Field Rules — write the typed `rules` array at the FormField level
    // (only when non-empty so unaffected fields stay clean). Sibling-field
    // references are rewritten from the builder's internal id to the stable key.
    if (Array.isArray(field.props.rules) && field.props.rules.length > 0) {
      apiField.rules = remapRuleFieldRefs(field.props.rules, keyForFieldId);
    }

    // Build configuration object
    const configuration = {};

    // Add options for select/radio/checkbox/tags
    if (field.props.options && Array.isArray(field.props.options)) {
      configuration.options = field.props.options.map(opt => ({
        value: opt.value,
        label: opt.label,
        ...(opt.color && { color: opt.color }),
        ...(opt.description && { description: opt.description }),
        ...(opt.icon && { icon: opt.icon }),
        ...(opt.disabled && { disabled: opt.disabled }),
        // Carry the cascade group tag back to the API option shape.
        ...(opt.group !== undefined && opt.group !== null && String(opt.group).trim() !== ''
          && { group: opt.group }),
      }));
    }

    // Add numeric constraints
    if (field.props.min !== undefined && field.props.min !== null) {
      configuration.min = field.props.min;
    }
    if (field.props.max !== undefined && field.props.max !== null) {
      configuration.max = field.props.max;
    }
    if (field.props.step !== undefined) {
      configuration.step = field.props.step;
    }

    // Add text constraints
    if (field.props.minLength !== undefined) {
      configuration.minLength = field.props.minLength;
    }
    if (field.props.maxLength !== undefined) {
      configuration.maxLength = field.props.maxLength;
    }
    if (field.props.pattern !== undefined) {
      configuration.pattern = field.props.pattern;
    }

    // Handle field-specific properties
    switch (field.type) {
      case 'tags':
        if (field.props.allowCustom !== undefined) configuration.allowCustomOptions = field.props.allowCustom;
        if (field.props.maxTags) configuration.maxTags = field.props.maxTags;
        break;

      case 'geolocation':
      case 'location':
        if (field.props.accuracy) {
          configuration.accuracyLevel = field.props.accuracy;
        }
        break;

      case 'header':
        if (field.props.text) {
          configuration.text = field.props.text;
        }
        if (field.props.level) {
          configuration.level = field.props.level;
        }
        if (field.props.align) {
          configuration.align = field.props.align;
        }
        break;

      case 'textarea':
        if (field.props.rows) {
          configuration.minRows = field.props.rows;
        }
        break;

      case 'rating':
        if (field.props.maxRating) {
          configuration.maxRating = field.props.maxRating;
        }
        if (field.props.ratingIcon) {
          configuration.ratingIcon = field.props.ratingIcon;
        }
        break;

      case 'slider':
        if (field.props.sliderLabels) {
          configuration.sliderLabels = field.props.sliderLabels;
        }
        break;

      case 'file':
      case 'image_camera':
      case 'image':
        if (field.props.acceptedFileTypes) {
          configuration.acceptedFileTypes = field.props.acceptedFileTypes;
        }
        if (field.props.maxFileSizeMB) {
          configuration.maxFileSizeMB = field.props.maxFileSizeMB;
        }
        if (field.props.maxFiles) {
          configuration.maxFiles = field.props.maxFiles;
        }
        break;

      case 'qr_scanner':
      case 'barcode_scanner':
        // Serialize validation rules to JSON string
        if (field.props.validationRules) {
          configuration.validationRules = typeof field.props.validationRules === 'string'
            ? field.props.validationRules
            : JSON.stringify(field.props.validationRules);
        }
        // Barcode format
        if (field.type === 'barcode_scanner' && field.props.barcodeFormat) {
          configuration.barcodeFormat = field.props.barcodeFormat;
        }
        break;

      case 'date':
      case 'time':
      case 'datetime': {
        // Map precision prop → correct API FieldType (overrides TYPE_MAP default of DateTime)
        const precision = field.props.precision || field.type;
        if (precision === 'date') apiField.type = FieldType.DATE;
        else if (precision === 'time') apiField.type = FieldType.TIME;
        else apiField.type = FieldType.DATE_TIME;
        if (field.props.autoCapture !== undefined) {
          configuration.autoCapture = field.props.autoCapture;
        }
        break;
      }

      case 'repeated_form':
        if (field.props.configuration) {
          configuration.repeatedFormSchemaId = field.props.configuration.repeatedFormSchemaId;
          if (field.props.configuration.minInstances !== undefined) {
            configuration.minInstances = field.props.configuration.minInstances;
          }
          if (field.props.configuration.maxInstances !== undefined) {
            configuration.maxInstances = field.props.configuration.maxInstances;
          }
          if (field.props.configuration.repeatedFormDisplayName) {
            configuration.repeatedFormDisplayName = field.props.configuration.repeatedFormDisplayName;
          }
        }
        break;

      case 'voice':
        if (field.props.enableTranscription !== undefined) {
          configuration.enableTranscription = field.props.enableTranscription;
        }
        if (field.props.transcriptionLanguage) {
          configuration.transcriptionLanguage = field.props.transcriptionLanguage;
        }
        if (field.props.maxDurationSeconds !== undefined) {
          configuration.maxDurationSeconds = field.props.maxDurationSeconds;
        }
        if (field.props.quality) {
          configuration.quality = field.props.quality;
        }
        break;
    }

    // Sample id lab-integration affordance: persist expectedLabResults on the sample id field.
    if (field.props.isSampleId && Array.isArray(field.props.expectedLabResults)) {
      const cleaned = field.props.expectedLabResults
        .filter(r => r && r.name && String(r.name).trim().length > 0)
        .map(r => ({
          name: String(r.name).trim(),
          ...(r.unit && { unit: String(r.unit).trim() }),
        }));
      if (cleaned.length > 0) {
        configuration.expectedLabResults = cleaned;
      }
    }

    // Only add configuration if it has properties
    if (Object.keys(configuration).length > 0) {
      apiField.configuration = configuration;
    }

    return apiField;
  });

  // Validate icon — ensure it's a valid Lucide icon name, fallback to default
  const validatedIcon = (icon && LUCIDE_ICON_MAP[icon]) ? icon : 'clipboard-list';

  return {
    title: title || 'Untitled Form',
    description: description || '',
    icon: validatedIcon,
    fields: apiFields,
    metadata: {
      createdWith: 'aQRate FormBuilder',
      version: '3.0.0',
      createdAt: new Date().toISOString(),
    }
  };
};

// Legacy compatibility functions (deprecated, will be removed in future versions)

/**
 * @deprecated Use convertFormSchemaDefinitionToFields instead
 * Convert JSON Schema format to internal field array format (OLD FORMAT)
 * This function is kept for backward compatibility with old schemas
 */
export const convertJsonSchemaToFields = (jsonSchema) => {
  // If it's already the new format, use the new converter
  if (jsonSchema && jsonSchema.fields && Array.isArray(jsonSchema.fields)) {
    return convertFormSchemaDefinitionToFields(jsonSchema);
  }

  // Otherwise, handle old JSON Schema format
  if (!jsonSchema || !jsonSchema.properties) {
    return [];
  }

  const { properties, required = [], fieldOrder = [] } = jsonSchema;
  const fields = [];

  const propertyKeys = fieldOrder.length > 0 ? fieldOrder : Object.keys(properties);

  propertyKeys.forEach((key) => {
    const property = properties[key];
    if (!property) return;

    const meta = property._meta || {};
    const fieldId = meta.fieldId || key;
    const originalType = meta.originalType || property.type;
    const isRequired = required.includes(key);

    let field = {
      id: fieldId,
      type: originalType,
      props: {
        name: key,
        label: property.title || key,
        placeholder: property.placeholder || '',
        required: isRequired,
        nameManuallySet: true,
      }
    };

    // Handle options
    if (property.enum && Array.isArray(property.enum)) {
      field.props.options = property.enum.map((value, idx) => ({
        value: value,
        label: property.enumNames?.[idx] || value
      }));
    }

    // Handle specific field types
    switch (originalType) {
      case 'geolocation':
      case 'location':
        field.props.accuracy = property.accuracy || 'high';
        break;

      case 'header':
        field.props.text = property.title || property.text || '';
        field.props.level = property.level || 'h3';
        field.props.align = property.align || 'left';
        break;

      case 'textarea':
        field.props.rows = property.rows || 4;
        break;
    }

    fields.push(field);
  });

  return fields;
};

/**
 * @deprecated Use convertFieldsToFormSchemaDefinition instead
 * Convert internal field array format to JSON Schema (OLD FORMAT)
 */
export const convertFieldsToJsonSchema = (fields) => {
  console.warn('convertFieldsToJsonSchema is deprecated. Use convertFieldsToFormSchemaDefinition instead.');

  const properties = {};
  const required = [];
  const fieldOrder = [];

  fields.forEach((field) => {
    const { id, type, props } = field;
    fieldOrder.push(id);

    if (props.required) {
      required.push(id);
    }

    const property = {
      title: props.label || id,
      type: 'string',
      _meta: {
        originalType: type,
        fieldId: id,
      }
    };

    if (props.options) {
      property.enum = props.options.map(opt => opt.value);
      property.enumNames = props.options.map(opt => opt.label);
    }

    properties[id] = property;
  });

  return {
    type: 'object',
    properties,
    required,
    fieldOrder,
  };
};
